import { useEffect, useState } from 'react'
import { Bell } from 'lucide-react'
import { useStore } from './store/useStore'
import Header from './components/layout/Header'
import Sidebar from './components/layout/Sidebar'
import AlertPanel from './components/alerts/AlertPanel'
import Dashboard from './components/dashboard/Dashboard'
import AnalysisPanel from './components/analysis/AnalysisPanel'
import SettingsUI from './components/settings/SettingsUI'
import { useAlertNotifications } from './hooks/useAlertNotifications'
import DisclaimerFooter from './components/layout/DisclaimerFooter'

export default function App() {
  const [showAdmin, setShowAdmin] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)
  const { activeTab, loadSymbols, loadAlerts, loadStatus, alerts, dismissedAlertIds } = useStore()
  const visibleAlertCount = alerts.filter((a) => !dismissedAlertIds.has(a.id)).length

  useAlertNotifications()

  useEffect(() => {
    loadSymbols()
    loadAlerts()
    loadStatus()

    const alertInterval = setInterval(() => {
      loadAlerts()
    }, 30_000)

    // Backgound refresh matching scheduler periodicity (Crypto: 5min, Stock/FX: 15min)
    const dataInterval = setInterval(async () => {
      const state = useStore.getState()
      if (!state.symbols) return

      const allSymbols = [
        ...state.symbols.crypto,
        ...state.symbols.stock,
        ...state.symbols.fx
      ]
      const activeIv = state.selectedInterval

      // 1. Refresh active-view predictions for all symbols
      await Promise.allSettled(allSymbols.map(sym => state.loadPrediction(sym, activeIv)))

      // 2. If a detail view is open, refresh its OHLCV and related timeframes
      const selSym = state.selectedSymbol
      if (selSym) {
        await Promise.allSettled([
          state.loadOHLCV(selSym, activeIv),
          state.loadPrediction(selSym, '1h'),
          state.loadPrediction(selSym, '4h'),
          state.loadPrediction(selSym, '1d')
        ])
      }
    }, 5 * 60_000) // Every 5 minutes

    const checkHash = () => {
      setShowAdmin(window.location.hash === '#admin')
    }
    window.addEventListener('hashchange', checkHash)
    checkHash()

    return () => {
      clearInterval(alertInterval)
      clearInterval(dataInterval)
      window.removeEventListener('hashchange', checkHash)
    }
  }, [loadSymbols, loadAlerts, loadStatus])

  return (
    <div className="flex flex-col h-screen bg-trading-bg overflow-hidden">
      {/* Header */}
      <div className="flex-none h-[60px] z-20">
        <Header onMenuClick={() => setSidebarOpen(true)} />
      </div>

      {/* Body row */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - desktop always visible */}
        <div className="hidden md:flex flex-none w-[200px] overflow-y-auto border-r border-trading-border">
          <Sidebar />
        </div>

        {/* Mobile sidebar drawer */}
        {sidebarOpen && (
          <>
            <div
              className="md:hidden fixed inset-0 z-30 bg-black/60"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="md:hidden fixed inset-y-0 left-0 z-40 w-[270px] overflow-y-auto shadow-2xl animate-slide-in-left">
              <Sidebar onClose={() => setSidebarOpen(false)} />
            </div>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-4">
          {showAdmin ? (
            <SettingsUI />
          ) : (
            <>
              {activeTab === 'dashboard' && <Dashboard />}
              {activeTab === 'analysis'  && <AnalysisPanel />}
            </>
          )}
        </main>

        {/* Alert panel - large screen only */}
        <div className="hidden lg:flex flex-none w-[280px] overflow-y-auto border-l border-trading-border">
          <AlertPanel />
        </div>

        {/* Mobile alert drawer */}
        {alertOpen && (
          <div
            className="lg:hidden fixed inset-0 z-30 bg-black/60"
            onClick={() => setAlertOpen(false)}
          />
        )}
        <div
          className={`lg:hidden fixed inset-y-0 right-0 z-40 w-[300px] shadow-2xl transition-transform duration-300 ${
            alertOpen ? 'translate-x-0 animate-slide-in-right' : 'translate-x-full pointer-events-none'
          }`}
        >
          <AlertPanel
            onNavigate={() => setAlertOpen(false)}
            onClose={() => setAlertOpen(false)}
          />
        </div>
      </div>

      {/* Disclaimer footer */}
      <DisclaimerFooter />

      {/* Mobile bottom bar */}
      <div className="md:hidden flex-none flex items-center justify-around border-t border-trading-border bg-trading-panel px-2 py-2 z-20">
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex flex-col items-center gap-0.5 px-4 py-1 rounded text-[10px] font-medium text-trading-muted transition-colors"
        >
          <span className="text-base leading-none">☰</span>
          銘柄
        </button>
        <button
          onClick={() => { useStore.getState().setActiveTab('dashboard'); setSidebarOpen(false) }}
          className={`flex flex-col items-center gap-0.5 px-4 py-1 rounded text-[10px] font-medium transition-colors ${
            activeTab === 'dashboard' ? 'text-trading-blue' : 'text-trading-muted'
          }`}
        >
          <span className="text-base leading-none">📊</span>
          ダッシュボード
        </button>
        <button
          onClick={() => { useStore.getState().setActiveTab('analysis'); setSidebarOpen(false) }}
          className={`flex flex-col items-center gap-0.5 px-4 py-1 rounded text-[10px] font-medium transition-colors ${
            activeTab === 'analysis' ? 'text-trading-blue' : 'text-trading-muted'
          }`}
        >
          <span className="text-base leading-none">📈</span>
          分析
        </button>
        <button
          onClick={() => setAlertOpen(true)}
          className="relative flex flex-col items-center gap-0.5 px-4 py-1 rounded text-[10px] font-medium text-trading-muted transition-colors"
        >
          <Bell size={16} />
          {visibleAlertCount > 0 && (
            <span className="absolute top-0 right-2 min-w-[14px] h-[14px] px-0.5 rounded-full bg-trading-red text-white text-[8px] font-bold flex items-center justify-center">
              {visibleAlertCount > 99 ? '99+' : visibleAlertCount}
            </span>
          )}
          アラート
        </button>
      </div>
    </div>
  )
}
